import React, { useCallback, useRef, useState } from "react";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { Flex, Text, TextField, IconButton, Card } from "@radix-ui/themes";
import { ArrowUpIcon } from "@radix-ui/react-icons";
import { RequestEmailMessage } from "../../entities/Message";
import { MessageAvatarSVG, MessageAvatarSVGProps } from "../loadSVG";
import { SvgRadius } from "../../entities/ChatSettingsV2";
import { MessageText } from "./MessageText";
import { validateEmail } from "../../utils/validation";

dayjs.extend(relativeTime);

export function RequestEmailMessageComponent({
  text,
  customSVG,
  svgRadius,
  data: { defaultEmail = "" },
  isLastMessage,
  onEscalationEmail,
  showAvatar,
  createdAt,
  showTimestamp,
  chatEnded,
}: RequestEmailMessage & {
  customSVG: string;
  svgRadius?: SvgRadius;
  isLastMessage: boolean;
  onEscalationEmail: (email: string) => Promise<void>;
  showAvatar: boolean;
  showTimestamp: boolean;
  chatEnded: boolean;
}) {
  const [email, setEmail] = useState(defaultEmail);
  const [loading, setLoading] = useState(false);
  const [showInput, setShowInput] = useState(isLastMessage);
  const [emailError, setEmailError] = useState<string>("");

  const inputRef = useRef<HTMLInputElement | null>(null);

  const handleSubmitEmail = useCallback(async () => {
    setEmailError("");

    const validation = validateEmail(email);
    if (!validation.isValid) {
      setEmailError(validation.error!);
      return;
    }

    setLoading(true);
    try {
      await onEscalationEmail(email.trim());
      setShowInput(false);
    } catch (error) {
      setEmailError("Failed to send email. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [email, onEscalationEmail]);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      const isMobile = /Mobi|Android/i.test(navigator.userAgent);
      if (!isMobile && event.key === "Enter" && !event.shiftKey) {
        handleSubmitEmail();
        event.preventDefault();
      }
    },
    [handleSubmitEmail],
  );

  const setInputRef = useCallback((el: HTMLInputElement | null) => {
    inputRef.current = el;
    el?.focus();
  }, []);

  return (
    <Flex pl={showAvatar ? "0" : "36px"} gap={"3"} align={"start"}>
      {showAvatar && (
        <MessageAvatarSVG path={customSVG} radius={svgRadius} alt={"System Message Avatar"} />
      )}

      <Flex direction={"column"} align={"start"} gap={"2"}>
        <Flex
          flexGrow={"1"}
          direction={"column"}
          gap={"1"}
          style={{
            background:
              "linear-gradient(260.74deg, rgba(249, 243, 227, 0.35) 4.89%, rgba(226, 234, 255, 0.35) 101.01%)",
            padding: "8px 12px",
            borderRadius: "8px",
          }}
        >
          <Flex pb={"4"}>
            <MessageText text={text} />
          </Flex>

          {showInput && !chatEnded && (
            <Card variant="classic" size={"3"} className={"qc-Chat__Requset_Email__Input"}>
              <TextField.Root
                ref={setInputRef}
                value={email}
                onChange={(event) => {
                  setEmail(event.target.value);
                  if (emailError) setEmailError("");
                }}
                placeholder="Enter your email"
                color={emailError ? "red" : undefined}
                autoComplete="email"
                onKeyDown={handleKeyDown}
                aria-label="Email"
              />

              <IconButton
                color="gray"
                variant="solid"
                radius="full"
                highContrast
                onClick={handleSubmitEmail}
                disabled={loading || !email.trim()}
                loading={loading}
                aria-label="Submit"
                title="Submit"
                type="submit"
              >
                <ArrowUpIcon width="15" height="15" />
              </IconButton>
            </Card>
          )}

          {emailError && (
            <Text size="1" color="red">
              {emailError}
            </Text>
          )}
        </Flex>

        {showTimestamp && (
          <Text size={"1"} color={"gray"}>
            {dayjs().diff(dayjs(createdAt), "day") > 1
              ? dayjs(createdAt).format("hh:mm:ss A")
              : dayjs(createdAt).fromNow()}
          </Text>
        )}
      </Flex>
    </Flex>
  );
}
